import type { SupportedLanguage } from '../i18n';

/**
 * Number locale of each UI language. EN uses `en-GB` (European audience: "€10,482.30").
 * Proposal, to be validated (C3 Q91).
 */
export const LOCALE_BY_LANGUAGE = { fr: 'fr-FR', en: 'en-GB' } as const satisfies Record<
  SupportedLanguage,
  string
>;

/** Input of {@link formatMoney}: a contract `Money` plus the UI language. */
export interface MoneyInput {
  /** Signed amount in minor units (cents for EUR and USD). A safe integer, never a float. */
  readonly amountMinor: number;
  /** ISO 4217 code of the account currency (Q27: never assume €). */
  readonly currency: string;
  readonly language: SupportedLanguage;
  /** Writes `+` on a positive amount, for results (P&L). Zero never gets a sign. */
  readonly signed?: boolean | undefined;
}

const CURRENCY_CODE = /^[A-Z]{3}$/;

function isDecimal(text: string): text is `${number}` {
  return /^-?\d+(?:\.\d+)?$/.test(text);
}

/** Sign of an amount; `-0` is zero. */
export function signOf(amountMinor: number): 'positive' | 'negative' | 'zero' {
  if (amountMinor > 0) return 'positive';
  if (amountMinor < 0) return 'negative';
  return 'zero';
}

/**
 * Formats money held as integer minor units with `Intl.NumberFormat` in the UI language
 * (standards §1, C3 R7). The amount goes to `Intl` as an exact decimal string, so no float
 * division ever rounds it.
 *
 * @throws {RangeError} when `amountMinor` is not a safe integer or `currency` is not ISO 4217.
 * @example formatMoney({ amountMinor: 1048230, currency: 'EUR', language: 'fr' }) // "10 482,30 €"
 */
export function formatMoney({ amountMinor, currency, language, signed }: MoneyInput): string {
  if (!Number.isSafeInteger(amountMinor)) {
    throw new RangeError(`amountMinor must be a safe integer, got ${String(amountMinor)}`);
  }
  if (!CURRENCY_CODE.test(currency)) {
    throw new RangeError(`currency must be an ISO 4217 code, got ${currency}`);
  }
  const format = new Intl.NumberFormat(LOCALE_BY_LANGUAGE[language], {
    style: 'currency',
    currency,
    signDisplay: signed === true ? 'exceptZero' : 'auto',
  });
  const digits = format.resolvedOptions().maximumFractionDigits ?? 2;
  const sign = signOf(amountMinor);
  const units = Math.abs(amountMinor)
    .toString()
    .padStart(digits + 1, '0');
  const decimal =
    digits === 0 ? units : `${units.slice(0, -digits)}.${units.slice(units.length - digits)}`;
  const value = `${sign === 'negative' ? '-' : ''}${decimal}`;
  // Intl reads a numeric string as an exact decimal (Intl.NumberFormat v3, ES2023).
  if (!isDecimal(value)) throw new RangeError(`Not a decimal: ${value}`);
  return format.format(value);
}
